import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Close } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'
import React, { useCallback } from 'react'

import { ContentFile } from '../../../services/api'
import { EditorTab } from '../hooks/useEditorTabs'

interface EditorTabBarProps {
  tabs: EditorTab[]
  activeFilename: string | null
  chapters: ContentFile[]
  hasUnsavedChanges: boolean
  onSelect: (filename: string) => void
  onClose: (filename: string) => void
  /** Drag & drop: move `fromFilename`'s tab to `toFilename`'s position. */
  onReorder: (fromFilename: string, toFilename: string) => void
  /** Book direction — an RTL book lays the tabs out right-to-left. */
  direction: 'rtl' | 'ltr'
  titleFontFamily?: string
}

interface TabButtonProps {
  filename: string
  title: string
  isActive: boolean
  showDirtyDot: boolean
  /** Hide the close button. Used when this is the last remaining tab — we always keep ≥1 open. */
  canClose: boolean
  onSelect: () => void
  onClose: () => void
  titleFontFamily?: string
}

const TabButton: React.FC<TabButtonProps> = ({
  filename,
  title,
  isActive,
  showDirtyDot,
  canClose,
  onSelect,
  onClose,
  titleFontFamily,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: filename })
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Middle-click closes the tab (unless this is the last one).
      if (e.button === 1 && canClose) {
        e.preventDefault()
        onClose()
      }
    },
    [canClose, onClose],
  )

  const handleCloseClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onClose()
    },
    [onClose],
  )

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onSelect}
      onMouseDown={handleMouseDown}
      role="tab"
      aria-selected={isActive}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: 0.5,
        minWidth: 80,
        maxWidth: 220,
        height: 32,
        cursor: 'pointer',
        userSelect: 'none',
        borderInlineEnd: '1px solid var(--toolbar-page-border)',
        bgcolor: isActive ? 'var(--bg-surface)' : 'transparent',
        borderTop: isActive ? '2px solid var(--sidebar-heading-active)' : '2px solid transparent',
        marginTop: '-1px',
        position: 'relative',
        opacity: isDragging ? 0.55 : 1,
        zIndex: isDragging ? 1 : 'auto',
        '&:hover': {
          bgcolor: isActive ? 'var(--bg-surface)' : 'var(--bg-hover)',
          '& .tab-close-btn': {
            opacity: 1,
          },
        },
      }}
    >
      {showDirtyDot ? (
        <Box
          aria-hidden
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: 'var(--sidebar-heading-active)',
            flexShrink: 0,
          }}
        />
      ) : null}
      <Typography
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontFamily: titleFontFamily || 'inherit',
          fontSize: 13,
          fontWeight: isActive ? 600 : 500,
          color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
        }}
        title={title}
      >
        {title}
      </Typography>
      {canClose && (
        <IconButton
          className="tab-close-btn"
          size="small"
          onClick={handleCloseClick}
          aria-label="Close tab"
          sx={{
            p: 0.25,
            opacity: isActive ? 0.7 : 0,
            color: 'var(--icon-default)',
            flexShrink: 0,
            transition: 'opacity 0.15s ease, background-color 0.15s ease',
            '&:hover': {
              opacity: 1,
              bgcolor: 'var(--bg-hover)',
              color: 'var(--icon-hover)',
            },
          }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  )
}

/** Display title for a tab (also used by the ⌥Tab switcher). */
export const tabTitleFor = (chapters: ContentFile[], filename: string): string => {
  return chapters.find((c) => c.filename === filename)?.title ?? filename
}

export default function EditorTabBar({
  tabs,
  activeFilename,
  chapters,
  hasUnsavedChanges,
  onSelect,
  onClose,
  onReorder,
  direction,
  titleFontFamily,
}: EditorTabBarProps) {
  // Require a small drag distance before a reorder starts, so plain clicks
  // (select) and middle-clicks (close) keep working untouched.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (over && active.id !== over.id) {
        onReorder(String(active.id), String(over.id))
      }
    },
    [onReorder],
  )

  if (tabs.length === 0) {
    return null
  }

  return (
    <Box
      role="tablist"
      dir={direction}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        bgcolor: 'var(--bg-app)',
        borderBottom: '1px solid var(--toolbar-page-border)',
        overflowX: 'auto',
        overflowY: 'hidden',
        flexShrink: 0,
        minHeight: 32,
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          height: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'var(--scrollbar-thumb)',
          borderRadius: 3,
        },
      }}
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={tabs.map((t) => t.filename)} strategy={horizontalListSortingStrategy}>
          {tabs.map((tab) => {
            const isActive = tab.filename === activeFilename
            return (
              <TabButton
                key={tab.filename}
                filename={tab.filename}
                title={tabTitleFor(chapters, tab.filename)}
                isActive={isActive}
                showDirtyDot={isActive && hasUnsavedChanges}
                canClose={tabs.length > 1}
                onSelect={() => onSelect(tab.filename)}
                onClose={() => onClose(tab.filename)}
                titleFontFamily={titleFontFamily}
              />
            )
          })}
        </SortableContext>
      </DndContext>
    </Box>
  )
}
